// src/hooks/usePresenceHeartbeat.js
// Anonymous presence heartbeat for the System Map's BetoniJerry hall. Every
// BEAT_MS while the tab is visible, POST {anonId, stage, authed} so the map can
// show live on-site / in-wizard / logged-in counts. Fire-and-forget, ungated,
// no new identifier (reuses betonijerry.sessionId). Aggregation + expiry live
// server-side in Redis. Pauses when the tab is hidden (visibilitychange).
import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useAuth } from "src/context/AuthContext";
import { getSessionId } from "src/utils/quote/quoteSession";
import { postPresence } from "src/utils/quote/quoteApi";

const BEAT_MS = 20000;

/** Pure: which presence stage a path represents. Exported for test. */
export function stageForPath(pathname) {
    return pathname && pathname.startsWith("/tilaa") ? "wizard" : "browsing";
}

export function usePresenceHeartbeat() {
    const location = useLocation();
    const { token } = useAuth();
    const stateRef = useRef({ stage: "browsing", authed: false });

    // Keep latest stage/authed in a ref, updated outside render (no dep array =
    // runs after every render), so the beat interval sends current values
    // without re-arming on each route change. Declared before the arm effect so
    // the ref is current before that effect's immediate first beat on mount.
    useEffect(() => {
        stateRef.current = { stage: stageForPath(location.pathname), authed: !!token };
    });

    useEffect(() => {
        let anonId;
        try {
            anonId = getSessionId();
        } catch {
            return undefined; // no id available — skip (getSessionId already self-falls-back)
        }
        let timer = null;
        const beat = () => postPresence({ anonId, ...stateRef.current });
        const arm = () => {
            if (timer) return; // idempotent
            if (document.visibilityState !== "visible") return;
            beat(); // immediate, so a visitor appears without a BEAT_MS delay
            timer = setInterval(beat, BEAT_MS);
        };
        const disarm = () => {
            if (timer) {
                clearInterval(timer);
                timer = null;
            }
        };
        const onVisibility = () => (document.visibilityState === "visible" ? arm() : disarm());
        arm();
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            disarm();
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);
}
